// ==============================|| CHAT TYPES (mediat de BE — api/chat/*) ||============================== //
// Modelul urmează contractul CityZen API: camerele sunt entități BE (int Id + conversationId
// chat-core), iar mesajele sunt corpul BRUT chat-core întors prin passthrough. Autorii vin din
// `sub`-uri Keycloak, rezolvate separat prin GET api/chat/users (directory).

/** GET api/chat/config — config pentru conectarea directă la chat-core (folosit la realtime, v2). */
export interface ChatConfig {
	wsUrl: string;
	httpBaseUrl: string;
	appScope: string;
}

export const CHAT_ROOM_TYPE = {
	Project: 1,
	Administration: 2,
	// Cameră creată manual de staff într-o administrație: membrii sunt EXCLUSIV cei adăugați (nu derivați
	// ca la Proiect/Administrație), toți din administrația camerei. Sync-ul de membri o lasă în pace.
	AdministrationGroup: 3,
	// Cameră de cartier: audiența o dau MEMBRII cartierului plus staff-ul administrației —
	// derivată, ca la proiecte.
	Neighborhood: 4
} as const;

/** ChatRoomDto — o cameră vizibilă utilizatorului, cu flag-urile de acces evaluate de BE. */
export interface ChatRoom {
	id: number;
	conversationId: string;
	roomTypeId: number; // 1 = Proiect, 2 = Administrație, 3 = Grup, 4 = Cartier
	projectId?: number | null;
	neighborhoodId?: number | null;
	administrationId: number;
	title?: string | null;
	createdAt: string;
	isArchived: boolean;
	canRead: boolean;
	canWrite: boolean;
	isMuted: boolean; // user „Restricted" (blocare scriere), NU mute notificări
	// utilizatorul curent poate restricționa membri DOAR în această cameră (moderatorul cartierului
	// cu dreptul de chat sau staff-ul cu moderare pe chatul de cartier)
	canModerate?: boolean;
	areNotificationsMuted: boolean;
	notificationsMutedUntil?: string | null;
	// Avatar camerei: imaginea proiectului (rooms de proiect) SAU avatarul administrației
	// (rooms de administrație). BE-ul unifică ambele în acest câmp; null → iconiță de tip.
	avatar?: ChatLightAsset | null;
	// Staff-ul care a CREAT o cameră custom (AdministrationGroup, tip 3), cu rolul lui curent. Null la
	// camerele de proiect/administrație (provizionate de platformă, fără owner). FE: rândul owner-ului
	// din grup nu poate fi scos (nici de el, nici de un moderator) — BE NU impune asta, e gardă FE.
	owner?: ChatParticipant | null;
}

export interface ChatLightAsset {
	id: number;
	fileName: string;
	publicUrl: string;
}

/** GET api/chat/users — rezolvarea unui `sub` Keycloak la nume/inițiale/avatar/rol. */
export interface ChatDirectoryUser {
	// id-ul intern CityZen al userului — permite acțiuni pe expeditor (ex. restricționare din mesaj)
	id?: number;
	userSub: string;
	displayName: string;
	initials: string;
	avatar?: string | null;
	role?: string | null;
}

/** Reacție agregată pe un mesaj (shape chat-core). */
export interface ChatReaction {
	emoji: string;
	count: number;
	reacted: boolean;
}

/** Obiect atașament (shape chat-core). Un mesaj poate avea unul sau o listă. */
export interface ChatAttachment {
	object: string;
	bucket?: string;
	name?: string;
	mime?: string;
	size?: number;
	downloadUrl?: string;
	durationSec?: number;
}

/** Payload-ul mesajului. Pentru text: `{ text }` (compus de RichTextEditor, randat cu SafeHtml). */
export interface ChatMessagePayload {
	text?: string;
	[key: string]: unknown;
}

/** Mesaj chat-core (corp brut passthrough din GET api/chat/rooms/{id}/messages). */
export interface ChatMessage {
	id: string;
	seq: number;
	senderSub: string;
	type: string;
	schema?: string | null;
	payload: ChatMessagePayload;
	attachment?: ChatAttachment | ChatAttachment[] | null;
	reactions?: ChatReaction[];
	isPinned?: boolean;
	pinnedBySub?: string;
	parentMessageId?: string | null;
	threadRootId?: string;
	replyCount?: number;
	editedAt?: string | null;
	createdAt: string;
}

/** Răspuns paginat de mesaje (cursor pe seq). */
export interface ChatMessagesPage {
	items: ChatMessage[];
	nextBeforeSeq: number | null;
	nextAfterSeq: number | null;
}

/** Fișier al unei camere (GET api/chat/rooms/{id}/attachments — listă paginată, BE-driven).
 *  `typeId`: 1 imagine, 2 video, 3 document, 4 altele. Preview/download prin `id` (auth, cu audit). */
export interface ChatRoomAttachment {
	id: number;
	chatRoomId: number;
	fileName: string;
	mime?: string | null;
	sizeBytes?: number | null;
	typeId: number;
	uploadedAt: string;
	messageId: string;
	threadRootId?: string | null;
	messageSeq?: number | null;
	uploadedBy?: ChatParticipant | null;
	isDeleted: boolean;
	deletedAt?: string | null;
	deletedBy?: ChatParticipant | null;
}

/** Utilizator afișat lângă ceva ce a făcut în chat — a încărcat/șters un fișier, a creat o cameră
 *  (BE: ChatParticipantDto). Doar ce trebuie pt. randare + rolul CURENT; ≠ ChatRoomMember (apartenență).
 */
export interface ChatParticipant {
	id: number;
	firstName?: string | null;
	lastName?: string | null;
	avatar?: string | null;
	role?: string | null;
}

/** Contoare pe tip + mărime totală (GET api/chat/rooms/{id}/attachments/summary). */
export interface ChatAttachmentSummary {
	total: number;
	images: number;
	videos: number;
	documents: number;
	other: number;
	totalSizeBytes: number;
}

/** Membru al unei camere (GET api/chat/rooms/{id}/users). */
export interface ChatRoomMember {
	id: number;
	firstName?: string | null;
	lastName?: string | null;
	avatar?: string | null;
	userSub?: string | null;
	role?: string | null;
	isOnline: boolean;
	// restricția membrului în această cameră — trimisă doar celor care o pot modera
	roomRestriction?: ChatRoomRestrictionType | null;
}

/** Restricții puse de un moderator doar într-o cameră: citire fără scriere sau fără acces. */
export type ChatRoomRestrictionType = 'mute' | 'ban';

/** Un membru restricționat într-o cameră (GET api/chat/rooms/{id}/restrictions). */
export interface ChatRoomRestriction {
	userId: number;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	avatar?: ChatLightAsset | null;
	type: ChatRoomRestrictionType;
	restrictedAt: string;
}

/** Durata de mute pentru notificările unei camere (1/7/30 zile; null = permanent). */
export type ChatMuteDays = 1 | 7 | 30;

/** Rezumatul ultimului mesaj dintr-o conversație (din GET api/chat/notifications). */
export interface ChatConversationLastMessage {
	seq: number;
	type: string;
	senderSub: string;
	createdAt: string;
	payload?: ChatMessagePayload;
	attachment?: ChatAttachment | ChatAttachment[] | null;
}

/** Sumar de conversație chat-core (GET api/chat/notifications) — unread + ultim mesaj + activitate. */
export interface ChatConversationSummary {
	id: string; // conversationId (UUID)
	unreadCount: number;
	isArchived?: boolean;
	updatedAt?: string;
	lastReadSeq?: number;
	lastMessage?: ChatConversationLastMessage | null;
}
